import { useId } from "react";
import type { ReactNode } from "react";
import { VisuallyHidden } from "./VisuallyHidden";
import styles from "./AmountField.module.css";

/**
 * One labelled money input.
 *
 * The accessible wiring is the part that is easy to get subtly wrong and impossible to see:
 * a real `<label>` bound by `id`, the hint and the error both named in `aria-describedby`
 * so a screen reader reads the guidance *and* the problem, and `aria-invalid` so the state
 * is exposed rather than implied by a red border.
 *
 * The id is generated per instance rather than derived from the field name, because the
 * desk renders one of these per biddable deal — a name-derived id would repeat down the
 * page and quietly bind several labels to the same input.
 *
 * `context` is appended to the visible label for assistive technology only. Every bid box
 * on the page reads "Your bid" visually; without the product name they would all share one
 * accessible name, and a screen-reader user moving between them could not tell which deal
 * they were about to bid on.
 *
 * The dollar sign is a decorative prefix: the value itself stays plain text, so paste,
 * autofill and `$5.50` all behave the way the platform's own input does. An error is never
 * carried by colour alone (canon `contracts/DESIGN-TOKENS.md`) — it has a marker glyph, the
 * word "Error" for assistive technology, and the message itself.
 */
export function AmountField({
  label,
  context,
  hint,
  error,
  value,
  onChange,
  disabled,
}: {
  label: string;
  /** Spoken after the label, e.g. the product this bid is for. */
  context?: string | undefined;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {context === undefined ? null : <VisuallyHidden> for {context}</VisuallyHidden>}
      </label>

      <div className={styles.control}>
        <span className={styles.prefix} aria-hidden="true">
          $
        </span>
        <input
          id={id}
          className={`${styles.input} figure`}
          type="text"
          // `decimal` rather than `numeric`: a bid may carry cents, and a numeric keypad
          // without a decimal point would make $1.50 impossible to type on a phone.
          inputMode="decimal"
          autoComplete="off"
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={error === undefined ? undefined : true}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      </div>

      {hint === undefined ? null : (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className={styles.error} id={errorId}>
          <svg className={styles.marker} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 4.6v4.2M8 11v.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <VisuallyHidden>Error: </VisuallyHidden>
          {error}
        </p>
      )}
    </div>
  );
}
